import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, UserPlus, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", display_name: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome!");
      navigate("/", { replace: true });
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 md:px-10 py-8 sm:py-14">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="mb-10">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
          Create account
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Join the market.
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-6 rounded-md border border-border bg-white p-6 md:p-8">
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">Display name</Label>
          <Input
            data-testid="register-name"
            value={form.display_name}
            onChange={(e) => set("display_name", e.target.value)}
            placeholder="e.g. Acme Studio"
            className="h-11 rounded-md"
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">Email</Label>
          <Input
            data-testid="register-email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@company.com"
            className="h-11 rounded-md"
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">Password</Label>
          <Input
            data-testid="register-password"
            type="password"
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            className="h-11 rounded-md"
            required
            minLength={6}
          />
          <div className="text-[10px] text-muted-foreground">At least 6 characters</div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">Phone (optional)</Label>
          <Input
            data-testid="register-phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+1 555 000 0000"
            className="h-11 rounded-md font-mono-brand"
          />
        </div>
        {error && (
          <div data-testid="register-error" className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full rounded-md h-11 gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4" /> Create account</>}
        </Button>
        <div className="text-xs text-center text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" data-testid="login-link" className="font-semibold hover:text-primary">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
